import React from "react";
import { Box, Center, Divider, HStack, Spinner, Tag, Text, VStack } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { HeaderWithStats } from "services/MetadataServices";
import JobCard from "modules/analyst/components/JobCard";

export interface JobListProps {
  jobs: Header[];
  isLoadingJobs: boolean;
  userId: string | undefined;
  copyingJobId: string | null;
  isDeleting: boolean;
  sharedByMap?: Record<string, string[]>;
  analystsList?: HeaderWithStats[];
  onJobClick?: (job: Header) => void;
  onDefinitionClick?: (job: Header) => void;
  onPastRunsClick?: (job: Header) => void;
  onToggleActive: (job: Header, isActive: boolean) => Promise<void>;
  onRunJob: (job: Header) => void;
  onCopyJob: (job: Header) => Promise<void>;
  onShareClick: (job: Header) => void;
  onDeleteClick: (job: Header) => void;
}

const JobList: React.FC<JobListProps> = ({
  jobs,
  isLoadingJobs,
  userId,
  copyingJobId,
  isDeleting,
  sharedByMap = {},
  analystsList = [],
  onJobClick,
  onDefinitionClick,
  onPastRunsClick,
  onToggleActive,
  onRunJob,
  onCopyJob,
  onShareClick,
  onDeleteClick,
}) => {
  const yourJobs = jobs.filter(j => j.author_id === userId);
  const othersJobs = jobs.filter(j => j.author_id !== userId);

  const renderSectionHeader = (title: string, count: number) => (
    <HStack spacing={3} mb={3}>
      <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
        {title}
      </Text>
      <Tag size="sm" colorScheme="purple" borderRadius="full" fontWeight="600" bg="purple.600" color="white">
        {count}
      </Tag>
      <Divider flex="1" borderColor="gray.200" />
    </HStack>
  );

  return (
    <Box height="100%" overflowY="auto">
      <Box maxW="900px" mx="auto">
        {isLoadingJobs ? (
          <Box height="200px" display="flex" alignItems="center" justifyContent="center">
            <Spinner size="sm" color="purple.500" thickness="4px" />
          </Box>
        ) : jobs.length === 0 ? (
          <Box height="200px" display="flex" alignItems="center" justifyContent="center">
            <Text color="gray.500" fontSize="md" mb={0}>
              No workflows match your search or filter.
            </Text>
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            {/* Your Workflows */}
            <Box>
              {renderSectionHeader("Your Workflows", yourJobs.length)}
              {yourJobs.length === 0 ? (
                <Center py={6}>
                  <Text color="gray.400" fontSize="sm" mb={0}>
                    Looks like you don't have any workflows yet. Create a new one to get started.
                  </Text>
                </Center>
              ) : (
                <VStack spacing={3} align="stretch">
                  {yourJobs.map(job => (
                    <JobCard
                      key={job.id}
                      job={job}
                      copyingJobId={copyingJobId}
                      isDeleting={isDeleting}
                      sharedByIds={job.id ? (sharedByMap[job.id] ?? []) : []}
                      analystsList={analystsList}
                      onJobClick={onJobClick}
                      onDefinitionClick={onDefinitionClick}
                      onPastRunsClick={onPastRunsClick}
                      onToggleActive={onToggleActive}
                      onRunJob={onRunJob}
                      onCopyJob={onCopyJob}
                      onShareClick={onShareClick}
                      onDeleteClick={onDeleteClick}
                    />
                  ))}
                </VStack>
              )}
            </Box>

            {/* Others' Workflows */}
            {othersJobs.length > 0 && (
              <Box>
                {renderSectionHeader("Others' Workflows", othersJobs.length)}
                <VStack spacing={2} align="stretch">
                  {othersJobs.map(job => (
                    <JobCard
                      key={job.id}
                      job={job}
                      copyingJobId={copyingJobId}
                      isDeleting={isDeleting}
                      sharedByIds={job.id ? (sharedByMap[job.id] ?? []) : []}
                      analystsList={analystsList}
                      onJobClick={onJobClick}
                      onDefinitionClick={onDefinitionClick}
                      onPastRunsClick={onPastRunsClick}
                      onToggleActive={onToggleActive}
                      onRunJob={onRunJob}
                      onCopyJob={onCopyJob}
                      onShareClick={onShareClick}
                      onDeleteClick={onDeleteClick}
                    />
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default JobList;
